import { marketObjects, MarketType } from 'constants/markets';
import { localizedRoutes } from 'constants/localizedRoutes';
import { getCategorySlugsByMarket } from 'graphql/productQueries';

export default class MarketUrlHelper {
    public static async translateUrlForMarket(
        url: URL,
        currentMarketCode: MarketType,
        newMarketCode: MarketType,
    ) : Promise<string | null> {
        const newMarket = marketObjects.find((m) => m.market === newMarketCode);

        if (!newMarket) {
            return null;
        }

        const path = url.pathname.startsWith('/sv') ? url.pathname.split('/sv')[1] : url.pathname;
        const notFound = path.startsWith('/not-found');

        if (notFound || isConsultantPage(path)) {
            return newMarket.frontendUrl;
        }

        const newPath = await this.translatePathForMarket(path, currentMarketCode, newMarketCode);
        const urlSuffix = `${url.search}${url.hash}`;

        return `${newMarket.frontendUrl}${newPath}${urlSuffix}`;
    }

    public static async translatePathForMarket(path: string, currentMarketCode: MarketType, newMarketCode: MarketType): Promise<string> {
        const productsPath = this.translateProductsPath(path, currentMarketCode, newMarketCode);

        if (productsPath.isProducts) {
            return productsPath.productsPath;
        }

        if (productsPath.isValid) {
            const categoryPath = await this.translateCategoryPath(path, currentMarketCode, newMarketCode);

            if (categoryPath.isCategory) {
                return categoryPath.fullPath ?? path;
            }

            if (categoryPath.isValid) {
                return `/${categoryPath.fullPath}/${getProductPath(path)}`;
            }
        }

        const infoPath = this.translateInfoPath(path, newMarketCode);

        if (infoPath.isValid) {
            return infoPath.infoPath ?? path;
        }

        return path;
    }

    public static translateProductsPath(
        path: string,
        currentMarket: MarketType,
        newMarket: MarketType,
    ) : {
            isValid: boolean;
            isProducts: boolean;
            productsPath: string;
        } {
        const currentTerm = localizedRoutes.product.rewrites[currentMarket];
        const newTerm = localizedRoutes.product.rewrites[newMarket];

        return {
            isValid: path.startsWith(currentTerm),
            isProducts: currentTerm === path,
            productsPath: newTerm,
        };
    }

    public static async translateCategoryPath(
        path: string,
        currentMarket: MarketType,
        newMarket: MarketType,
    ): Promise<{
            isValid: boolean;
            isCategory: boolean | null;
            categoryPath: string | null;
            fullPath: string | null;
        }> {
        const productsTerm = localizedRoutes.product.rewrites[currentMarket];
        const productsPath = this.translateProductsPath(path, currentMarket, newMarket);

        if (productsPath.isValid && !productsPath.isProducts) {
            const { data: categorySlugsData } = await getCategorySlugsByMarket(currentMarket);
            const categorySlugs = categorySlugsData?.categorySlugs?.items ?? [];
            const sortedCategorySlugs = sortCategorySlugs(categorySlugs);

            const { data: newCategorySlugsData } = await getCategorySlugsByMarket(newMarket);
            const newCategorySlugs = newCategorySlugsData?.categorySlugs?.items ?? categorySlugs;

            for (const [i, slug] of sortedCategorySlugs.entries()) {
                if (path.startsWith(`${productsTerm}/${slug}`)) {
                    const index = categorySlugs.findIndex((s) => s === slug);
                    const categorySlug = newCategorySlugs[index] ?? '';

                    return {
                        isValid: true,
                        isCategory: path === `${productsTerm}/${slug}`,
                        categoryPath: categorySlug,
                        fullPath: `${productsPath.productsPath}/${categorySlug}`,
                    };
                }
            }
        }

        return {
            isValid: false,
            isCategory: null,
            categoryPath: null,
            fullPath: null,
        };
    }

    public static translateInfoPath(
        path: string,
        newMarket: MarketType,
    ): {
            isValid: boolean;
            infoPath: string | null;
        } {
        const infoPath = getInfoPath(path, newMarket);

        return {
            isValid: infoPath !== null,
            infoPath,
        };
    }
}

const sortCategorySlugs = (slugs: string[]): string[] => (
    [...slugs].sort((firstSlug, secondSlug) => (
        secondSlug.split('/').length - firstSlug.split('/').length))
);

const getProductPath = (path: string): string => {
    const splitPath = path.split('/');

    return splitPath[splitPath.length - 1];
};

const isConsultantPage = (path: string): boolean => {
    const pagePath = path.split('/');
    if (pagePath[1] === 'consultant' && pagePath.length === 3) {
        return true;
    }

    return false;
};

const getInfoPath = (path: string, newMarket: MarketType): string | null => {
    if (path.startsWith('/beauty-launch-')) {
        switch (newMarket) {
            case 'sefi':
                return '/beauty-launch-sv-fi';

            case 'se':
                return '/beauty-launch-sv';

            default:
                return `/beauty-launch-${newMarket}`;
        }
    }

    if (path.startsWith('/kontakt-') || path.startsWith('/contact-us')) {
        switch (newMarket) {
            case 'fi':
                return '/contact-us';

            case 'sefi':
                return '/contact-us';

            case 'se':
                return '/kontakt-sv';

            default:
                return `/kontakt-${newMarket}`;
        }
    }

    return null;
};
